/**
 * 示意地图上的站点和站名怎么画（2026-09-27 巡检 P3：两站离得很近——家和附近的咖啡馆几乎重合——站名叠在一起，
 * “家”压在“星球居民开的小咖啡馆”下面、也压在交通工具标记下面，另一站的圆点挤在标记旁边）。
 * - 同一个站只画一次：来回一趟时“家”既是出门那段的起点、又是回家那段的终点，原来每个站都画了两遍。
 *   “同一个站”的口径见 samePlace：名字去掉首尾空格后相同（中间的空格照算），且经纬度都按 toFixed(5) 取到小数点后 5 位（约 1 米）后相同。
 *   同名但不同点的两站不合并（只差几米的也不合并，交给下面“挨得近就缩成小点”）；同一点但不同名的两站也不合并。
 * - 按先后放：places 里标了 primary 的（旅途页标“当前站”：TA 在的地方，或正在去的地方）先放，其余照原顺序。
 *   离已经放下的站不到 CLOSE_PX 像素的，缩成小点、不写站名——交通工具标记半径 26 像素，停在站上时，
 *   再近的站圆点就挤在标记边上，站名也会压到标记和旁边的站名；
 *   不近、但站名外框会压到已经放下的站名的，收起站名，圆点照常。
 *   两种收起的站名都交给读屏（页面上看不见，见 MapSurface）。
 * - 其余照旧：远距离、站名互不相压时，画法和原来完全一样。
 * - 站名外框按字数估：中文等全角字 12 像素宽、其余 7.5；高 17，从站点上方 26 像素到上方 9 像素（12px 粗体实际量出来的）。
 *   只拿站名和站名比，页面对站名整体的平移（旅途页整体上移 18 像素）不影响结果。
 */
import type { LatLng } from "@/shared/contracts";

export interface MapPlace {
  id: string;
  at: LatLng;
  name: string;
  /** 当前站：先放它，挨得近的别的站让它（缩成小点）。不给就按原顺序。 */
  primary?: boolean;
}

/** label：圆点加站名（原来的画法）；quiet：圆点照常、不写站名；dot：缩成小点、不写站名。 */
export type PlaceMode = "label" | "quiet" | "dot";

export interface PlacedPlace {
  id: string;
  name: string;
  x: number;
  y: number;
  mode: PlaceMode;
  /** 缩成小点时，挨着的那一站（读屏说“就在某某旁边”）。 */
  nextTo: string | null;
}

/** 两站投影后的距离小于这么多像素，后放的那站缩成小点（交通工具标记半径 26 + 站点圆半径 8）。 */
export const CLOSE_PX = 34;
/** 站名外框之间至少留这么多像素（字的描边各占 2 像素）。 */
export const LABEL_GAP = 2;

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 站名宽度估计：全角字 12 像素，其余 7.5 像素（12px 粗体）。 */
export function labelWidth(name: string): number {
  let width = 0;
  for (const ch of name) width += (ch.codePointAt(0) ?? 0) > 0xff ? 12 : 7.5;
  return width;
}

/** 站名外框（地图坐标，没算页面对站名的整体平移）。 */
export function labelBox(x: number, y: number, name: string): Box {
  const w = labelWidth(name);
  return { x: x - w / 2, y: y - 26, w, h: 17 };
}

/** 同一个站（去重口径，见文件头）：名字去掉首尾空格后相同，且经纬度 toFixed(5) 后相同。 */
export function samePlace(a: MapPlace, b: MapPlace): boolean {
  const key = (place: MapPlace) => `${place.name.trim()}|${place.at.lat.toFixed(5)}|${place.at.lng.toFixed(5)}`;
  return key(a) === key(b);
}

export function boxesOverlap(a: Box, b: Box, gap = LABEL_GAP): boolean {
  return a.x < b.x + b.w + gap && b.x < a.x + a.w + gap && a.y < b.y + b.h + gap && b.y < a.y + a.h + gap;
}

export function layoutPlaces(places: readonly MapPlace[], project: (point: LatLng) => { x: number; y: number }): PlacedPlace[] {
  const ordered = [...places.filter((place) => place.primary), ...places.filter((place) => !place.primary)];
  const kept: MapPlace[] = [];
  const placed: PlacedPlace[] = [];
  const boxes: Box[] = [];
  for (const place of ordered) {
    if (kept.some((other) => samePlace(other, place))) continue;
    kept.push(place);
    const { x, y } = project(place.at);
    const near = placed.find((other) => other.mode !== "dot" && Math.hypot(other.x - x, other.y - y) < CLOSE_PX);
    if (near) {
      placed.push({ id: place.id, name: place.name, x, y, mode: "dot", nextTo: near.name });
      continue;
    }
    const box = labelBox(x, y, place.name);
    if (boxes.some((other) => boxesOverlap(other, box))) {
      placed.push({ id: place.id, name: place.name, x, y, mode: "quiet", nextTo: null });
      continue;
    }
    boxes.push(box);
    placed.push({ id: place.id, name: place.name, x, y, mode: "label", nextTo: null });
  }
  return placed;
}

/** 读屏用的一句话：图上没写出名字的站；都写出来了就是 null。 */
export function unlabeledNote(placed: readonly PlacedPlace[]): string | null {
  const names = placed.filter((place) => place.mode !== "label").map((place) => (place.nextTo ? `${place.name}（就在${place.nextTo}旁边）` : place.name));
  return names.length ? `图上没写出名字的地点：${names.join("、")}` : null;
}
